import './PreResultsGrid.css';
import { useId } from 'preact/hooks';
import { KeyValueRow } from '../../components/KeyValueRow';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import type { Geography } from '../../contracts/static';
import { t } from '../../copy/strings';
import { fmtVoters } from '../../format/numbers';
import { PLACES } from '../../format/places';
import { openProfile } from '../../state/actions';
import { useConfig, useLayout, useStatics, useView } from '../../state/hooks';
import { partyOf } from '../../theme/parties';

/** Σ `electorate_ref` das 27 UFs e do Exterior (§7.11 bloco 2); `null` sem `Geography` (o valor mostra "–"). */
export function electorateOf(geo: Geography | null | undefined): number | null {
  if (!geo) return null;
  let sum = 0;
  let found = 0;
  for (const p of geo.places) {
    if (!Object.hasOwn(PLACES, p.code) || !Number.isFinite(p.electorate_ref)) continue;
    sum += p.electorate_ref;
    found++;
  }
  return found > 0 ? sum : null;
}

/**
 * Os dois candidatos antes dos votos (§7.11, F139): esqueleto com dados reais, na ordem 13 | 22, que nunca depende do
 * feed (cadastro estático; com snapshot, `view.candidates`). Cada célula é um `<button>` com o nome visível como nome
 * acessível e `hero.leader.aria` em `aria-describedby` (§15.4.2); na TV, retratos de 64 e células não clicáveis.
 */
export function PreResultsGrid() {
  const view = useView();
  const statics = useStatics();
  // o eleitorado do Brasil vem do snapshot (4 KB, o 1º arquivo da noite); a soma da geografia (224 KB, pedida depois do
  // 1º número) só sem ele. Esperar a geografia deixava este valor, o maior texto da tela antes das 17h, para depois de
  // 4 s no celular do CI: era o LCP de #/presidente (DECISOES §38)
  const brElectorate = view?.national.electorate;
  const electorate = brElectorate && brElectorate > 0 ? brElectorate : electorateOf(statics.geography);
  const photos = useConfig().features.photos;
  const tv = useLayout().mode === 'tv';
  const id = useId();
  const list = view?.candidates ?? statics.candidates?.president ?? null;
  if (list === null) return null;
  const size = tv ? 64 : 48;
  return (
    <div class="pre-results-grid" data-testid="pre-results-grid">
      <ul class="pre-results-grid__cells">
        {list.map((c, i) => {
          const party = partyOf(c);
          const descId = `${id}-d${i}`;
          const body = (
            <>
              <Portrait candidate={c} photo={photos ? c.photo : null} size={size} shape="round" loading="eager" />
              <span class="pre-results-grid__name">{c.display_name}</span>
              <PartyTag candidate={c} />
            </>
          );
          return (
            <li key={i} class="pre-results-grid__item" data-number={c.number}>
              {tv ? (
                <div class="pre-results-grid__cell">{body}</div>
              ) : (
                <button
                  type="button"
                  class="pre-results-grid__cell"
                  aria-describedby={descId}
                  onClick={() => openProfile(c.number, 'president', 'list')}
                >
                  {body}
                  <span id={descId} hidden>
                    {t('hero.leader.aria', { NOME: c.display_name, SIGLA: party.display, NUM: c.number })}
                  </span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <dl class="pre-results-grid__facts">
        <KeyValueRow label={t('pre.candidacies')} value={list.length} />
        <KeyValueRow label={t('pre.electorate')} value={electorate} format={fmtVoters} />
      </dl>
    </div>
  );
}
